import React, { useState } from 'react';
import { X, Eye, EyeOff, User, Building2, Lock, CheckCircle2 } from 'lucide-react';

// ── Password input — defined at module level to keep stable identity ──────────
const PasswordInput = ({ value, onChange, show, onToggle, placeholder, hasError }) => (
  <div style={{ position: 'relative' }}>
    <input
      type={show ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        width: '100%', padding: '0.6rem 3rem 0.6rem 0.9rem',
        border: `1.5px solid ${hasError ? '#ef4444' : '#d1d5db'}`,
        borderRadius: '0.6rem', fontSize: '1.05rem',
        outline: 'none', background: '#fff',
        boxSizing: 'border-box', fontFamily: 'inherit',
        transition: 'border-color 0.15s',
        color: '#111827'
      }}
      onFocus={e => { if (!hasError) e.target.style.borderColor = '#6366f1'; }}
      onBlur={e  => { if (!hasError) e.target.style.borderColor = '#d1d5db'; }}
    />
    <button
      type="button" onClick={onToggle}
      style={{
        position: 'absolute', right: '0.72rem', top: '50%',
        transform: 'translateY(-50%)', background: 'none',
        border: 'none', cursor: 'pointer', color: '#9ca3af',
        padding: 0, display: 'flex', lineHeight: 1
      }}
    >
      {show ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  </div>
);

const InstructorSignupForm = ({ onClose, onSave, onSubmit, prefilledEmail }) => {
  const [formData, setFormData] = useState({
    firstName: '', lastName: '',
    email: prefilledEmail || '',
    countryCode: '+44', phoneNumber: '',
    password: '', confirmPassword: '',
    businessName: '', address: '', postcode: '',
    termsAccepted: false
  });
  const [errors, setErrors]                           = useState({});
  const [showPassword, setShowPassword]               = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitted, setSubmitted]                     = useState(false);

  const set = (field, value) => {
    setFormData(p => ({ ...p, [field]: value }));
    if (errors[field]) setErrors(p => ({ ...p, [field]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!formData.firstName.trim())    e.firstName    = 'Required';
    if (!formData.lastName.trim())     e.lastName     = 'Required';
    if (!formData.email.trim())        e.email        = 'Required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) e.email = 'Invalid email';
    if (!formData.phoneNumber.trim())  e.phoneNumber  = 'Required';
    else if (!/^\d{10}$/.test(formData.phoneNumber.trim()))   e.phoneNumber = 'Phone number must be exactly 10 digits';
    if (!formData.password) {
      e.password = 'Required';
    } else {
      const pwd = formData.password;
      const errors_pwd = [];
      if (pwd.length < 8)             errors_pwd.push('at least 8 characters');
      if (!/[A-Z]/.test(pwd))         errors_pwd.push('one uppercase letter (A-Z)');
      if (!/[0-9]/.test(pwd))         errors_pwd.push('one number (0-9)');
      if (!/[!@#$%^&*]/.test(pwd))    errors_pwd.push('one special character (!@#$%^&*)');
      if (errors_pwd.length > 0)
        e.password = `Password must contain: ${errors_pwd.join(', ')}.`;
    }
    if (!formData.confirmPassword)     e.confirmPassword = 'Required';
    else if (formData.password !== formData.confirmPassword) e.confirmPassword = 'Passwords do not match';
    if (!formData.businessName.trim()) e.businessName = 'Required';
    if (!formData.address.trim())      e.address      = 'Required';
    if (!formData.postcode.trim())     e.postcode     = 'Required';
    if (!formData.termsAccepted)       e.termsAccepted = 'You must accept the Terms and Conditions';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const { confirmPassword, countryCode, phoneNumber, ...rest } = formData;
    const phone = `${countryCode}${phoneNumber.trim()}`;
    const payload = {
      ...rest,
      phone,
      name: `${formData.firstName} ${formData.lastName}`,
      registrationDate: new Date().toISOString().split('T')[0],
      status: 'pending', userType: 'instructor'
    };
    if (onSubmit) onSubmit(payload);
    else if (onSave) onSave(payload);
    setSubmitted(true);
  };

  // ── Shared field styles ──────────────────────────────────────────────────
  const inputStyle = (field) => ({
    width: '100%', padding: '0.6rem 0.9rem',
    border: `1.5px solid ${errors[field] ? '#ef4444' : '#d1d5db'}`,
    borderRadius: '0.6rem', fontSize: '1.05rem',
    outline: 'none', background: field === 'email' && prefilledEmail ? '#f9fafb' : '#fff',
    boxSizing: 'border-box', fontFamily: 'inherit',
    color: '#111827',
    cursor: field === 'email' && prefilledEmail ? 'not-allowed' : 'auto',
    transition: 'border-color 0.15s'
  });

  const Label = ({ children }) => (
    <label style={{ fontSize: '0.9rem', fontWeight: 600, color: '#374151',
      letterSpacing: '0.03em', textTransform: 'uppercase', marginBottom: '0.3rem',marginTop: '1.0rem',
      display: 'block' }}>
      {children}
    </label>
  );

  const Err = ({ field }) => errors[field]
    ? <span style={{ fontSize: '0.84rem', color: '#ef4444', marginTop: '0.24rem',
        display: 'block' }}>{errors[field]}</span>
    : null;

  const SectionHead = ({ icon: Icon, label }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.48rem',
      borderBottom: '1.5px solid #e5e7eb', paddingBottom: '0.42rem',
      marginBottom: '0.78rem' }}>
      <Icon size={16} color="#6366f1" />
      <span style={{ fontSize: '0.84rem', fontWeight: 700, color: '#6366f1',
        letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</span>
    </div>
  );

  const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.72rem' };
  const field  = { display: 'flex', flexDirection: 'column' };

  return (
    <div style={{
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      background: '#fff', borderRadius: '1.2rem',
      boxShadow: '0 4px 38px rgba(0,0,0,0.10)',
      width: '100%', maxWidth: '672px',
      margin: '0 auto', overflow: 'hidden'
    }}>

      {/* ── Header ───────────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
        padding: '1.32rem 1.68rem', display: 'flex',
        justifyContent: 'space-between', alignItems: 'center'
      }}>
        <div>
          <p style={{ margin: 0, fontSize: '0.84rem', color: 'rgba(255,255,255,0.7)',
            letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600 }}>
            DriveZoe
          </p>
          <h2 style={{ margin: '0.12rem 0 0', fontSize: '1.38rem', fontWeight: 700,
            color: '#fff', letterSpacing: '-0.01em' }}>
            Instructor Registration
          </h2>
        </div>
        {onClose && (
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.15)', border: 'none',
            borderRadius: '0.6rem', cursor: 'pointer', color: '#fff',
            padding: '0.42rem', display: 'flex', alignItems: 'center',
            transition: 'background 0.15s'
          }}>
            <X size={22} />
          </button>
        )}
      </div>

      {/* ── Form ─────────────────────────────────────────────────────── */}
      <form onSubmit={handleSubmit} style={{ padding: '1.32rem 1.68rem 1.5rem' }}>

        {/* Personal */}
        <div style={{ marginBottom: '1.08rem' }}>
          <SectionHead icon={User} label="Personal Information" />
          <div style={{ ...grid2, marginBottom: '0.72rem' }}>
            <div style={field}>
              <Label>First Name *</Label>
              <input style={inputStyle('firstName')} value={formData.firstName}
                onChange={e => set('firstName', e.target.value)}
                onFocus={e => { if (!errors.firstName) e.target.style.borderColor='#6366f1'; }}
                onBlur={e  => { if (!errors.firstName) e.target.style.borderColor='#d1d5db'; }} />
              <Err field="firstName" />
            </div>
            <div style={field}>
              <Label>Last Name *</Label>
              <input style={inputStyle('lastName')} value={formData.lastName}
                onChange={e => set('lastName', e.target.value)}
                onFocus={e => { if (!errors.lastName) e.target.style.borderColor='#6366f1'; }}
                onBlur={e  => { if (!errors.lastName) e.target.style.borderColor='#d1d5db'; }} />
              <Err field="lastName" />
            </div>
          </div>
          <div style={{ ...grid2, marginBottom: '0.72rem' }}>
            <div style={field}>
              <Label>Email *</Label>
              <input style={inputStyle('email')} type="email" value={formData.email}
                disabled={!!prefilledEmail}
                onChange={e => set('email', e.target.value)}
                onFocus={e => { if (!errors.email && !prefilledEmail) e.target.style.borderColor='#6366f1'; }}
                onBlur={e  => { if (!errors.email) e.target.style.borderColor='#d1d5db'; }} />
              <Err field="email" />
            </div>
            <div style={field}>
              <Label>Phone *</Label>
              {/* Joined country-code + number input */}
              <div style={{
                display: 'flex', borderRadius: '0.6rem', overflow: 'hidden',
                border: `1.5px solid ${errors.phoneNumber ? '#ef4444' : '#d1d5db'}`,
                transition: 'border-color 0.15s'
              }}
                onFocusCapture={e => { e.currentTarget.style.borderColor = errors.phoneNumber ? '#ef4444' : '#6366f1'; }}
                onBlurCapture={e  => { e.currentTarget.style.borderColor = errors.phoneNumber ? '#ef4444' : '#d1d5db'; }}
              >
                {/* Country code selector */}
                <select
                  value={formData.countryCode}
                  onChange={e => set('countryCode', e.target.value)}
                  style={{
                    padding: '0.6rem 0.5rem', fontSize: '1.05rem',
                    border: 'none', borderRight: '1.5px solid #e5e7eb',
                    background: '#f9fafb', color: '#374151',
                    fontFamily: 'inherit', outline: 'none', cursor: 'pointer',
                    flexShrink: 0, appearance: 'none', minWidth: '72px',
                    textAlign: 'center', fontWeight: 600
                  }}
                >
                  <option value="+44">🇬🇧 +44</option>
                  <option value="+1">🇺🇸 +1</option>
                  <option value="+353">🇮🇪 +353</option>
                  <option value="+33">🇫🇷 +33</option>
                  <option value="+49">🇩🇪 +49</option>
                  <option value="+91">🇮🇳 +91</option>
                  <option value="+61">🇦🇺 +61</option>
                  <option value="+971">🇦🇪 +971</option>
                </select>
                {/* Phone number */}
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={e => set('phoneNumber', e.target.value.replace(/\D/g, ''))}
                  placeholder="7700 900000"
                  style={{
                    flex: 1, padding: '0.6rem 0.9rem', fontSize: '1.05rem',
                    border: 'none', outline: 'none', background: '#fff',
                    fontFamily: 'inherit', color: '#111827', minWidth: 0
                  }}
                />
              </div>
              <Err field="phoneNumber" />
            </div>
          </div>
          <div style={grid2}>
            <div style={field}>
              <Label>Password *</Label>
              <PasswordInput value={formData.password} show={showPassword}
                onChange={e => set('password', e.target.value)}
                onToggle={() => setShowPassword(p => !p)}
                placeholder="At least 8 chars, 1 uppercase, 1 number, 1 special (!@#$%^&*)" hasError={!!errors.password} />
              <Err field="password" />
            </div>
            <div style={field}>
              <Label>Confirm Password *</Label>
              <PasswordInput value={formData.confirmPassword} show={showConfirmPassword}
                onChange={e => set('confirmPassword', e.target.value)}
                onToggle={() => setShowConfirmPassword(p => !p)}
                hasError={!!errors.confirmPassword} />
              <Err field="confirmPassword" />
              <Label></Label>
            </div>
          </div>
        </div>

        {/* Business */}
        <div style={{ marginBottom: '1.08rem' }}>
          <SectionHead icon={Building2} label="Business Information" />
          <div style={{ marginBottom: '0.72rem', ...field }}>
            <Label>Business Name *</Label>
            <input style={inputStyle('businessName')} value={formData.businessName}
              placeholder="Your Driving School Name"
              onChange={e => set('businessName', e.target.value)}
              onFocus={e => { if (!errors.businessName) e.target.style.borderColor='#6366f1'; }}
              onBlur={e  => { if (!errors.businessName) e.target.style.borderColor='#d1d5db'; }} />
            <Err field="businessName" />
          </div>
          <div style={grid2}>
            <div style={{ ...field, gridColumn: '1 / span 1' }}>
              <Label>Business Address *</Label>
              <input style={inputStyle('address')} value={formData.address}
                placeholder="123 Main Street"
                onChange={e => set('address', e.target.value)}
                onFocus={e => { if (!errors.address) e.target.style.borderColor='#6366f1'; }}
                onBlur={e  => { if (!errors.address) e.target.style.borderColor='#d1d5db'; }} />
              <Err field="address" />
            </div>
            <div style={field}>
              <Label>Postcode *</Label>
              <input style={inputStyle('postcode')} value={formData.postcode}
                placeholder="SW1A 1AA"
                onChange={e => set('postcode', e.target.value)}
                onFocus={e => { if (!errors.postcode) e.target.style.borderColor='#6366f1'; }}
                onBlur={e  => { if (!errors.postcode) e.target.style.borderColor='#d1d5db'; }} />
              <Err field="postcode" />
            </div>
          </div>
        </div>

        {/* Terms + Submit — grouped together, no gap */}
        <div style={{
          borderTop: '1.5px solid #e5e7eb', paddingTop: '1.02rem',
          display: 'flex', flexDirection: 'column', gap: '0.9rem'
        }}>
          {/* Terms */}
          <label style={{ display: 'flex', alignItems: 'flex-start',
            gap: '0.72rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={formData.termsAccepted}
              onChange={e => set('termsAccepted', e.target.checked)}
              style={{ marginTop: '2px', width: '18px', height: '18px',
                accentColor: '#6366f1', flexShrink: 0, cursor: 'pointer' }} />
            <span style={{ fontSize: '0.96rem', color: '#4b5563', lineHeight: 1.5 }}>
              I have read and agree to the{' '}
              <a href="https://drivezoe.com/terms" target="_blank" rel="noopener noreferrer"
                style={{ color: '#6366f1', fontWeight: 600 }}>
                Terms and Conditions
              </a>{' '}*
            </span>
          </label>
          {errors.termsAccepted && (
            <span style={{ fontSize: '0.84rem', color: '#ef4444', marginTop: '-0.48rem' }}>
              {errors.termsAccepted}
            </span>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '0.72rem', justifyContent: 'flex-end' }}>
            {onClose && (
              <button type="button" onClick={onClose} style={{
                padding: '0.66rem 1.32rem', borderRadius: '0.6rem',
                border: '1.5px solid #d1d5db', background: '#fff',
                color: '#374151', fontSize: '1.05rem', fontWeight: 500,
                cursor: 'pointer', fontFamily: 'inherit'
              }}>
                Cancel
              </button>
            )}
            <button type="submit" style={{
              padding: '0.66rem 1.68rem', borderRadius: '0.6rem',
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              border: 'none', color: '#fff', fontSize: '1.05rem',
              fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: '0.48rem',
              boxShadow: '0 2px 8px rgba(99,102,241,0.35)',
              transition: 'opacity 0.15s'
            }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              <CheckCircle2 size={19} />
              Submit Registration
            </button>
          </div>
        </div>

      </form>
    </div>
  );
};

export default InstructorSignupForm;